/**
 * SCard - Server component for creating styled card containers
 * This is the server-rendered version of the Card component for use in Server Components
 */
type CardProps = {
  /** Content to render inside the card */
  children: React.ReactNode;
  /** CSS class for border radius (e.g., "rounded-3xl") */
  rounded?: string;
  /** Additional CSS classes to apply */
  className?: string;
  /** CSS class for border outline (e.g., "outline-primary") */
  outline?: string;
  /** CSS class for shadow effect (e.g., "shadow-2xl") */
  shadow?: string;
  /** Optional header text to display at the top of the card */
  header?: string;
  /** CSS class for padding (e.g., "p-12") */
  padding?: string;
  /** CSS class for height (e.g., "h-auto") */
  height?: string;
  /** CSS class for width (e.g., "w-125") */
  width?: string;
  /** Optional ref for DOM access */
  ref?: React.RefObject<HTMLDivElement | null>;
  /** CSS classes for alignment (e.g., "justify-center items-center") */
  justify?: string;
  /** CSS class for outer margin (e.g., "mt-6") */
  marginOuter?: string;
};

/**
 * Creates a styled card container with configurable appearance
 * Server component version for use in RSC contexts
 *
 * @param children - Content to render inside the card
 * @param rounded - Border radius class (default: "rounded-3xl")
 * @param outline - Border outline class (default: "outline-primary")
 * @param shadow - Shadow effect class (default: "shadow-2xl")
 * @param className - Additional CSS classes
 * @param header - Optional header text for the card
 * @param padding - Padding class (default: "p-12")
 * @param height - Height class (default: "h-auto")
 * @param width - Width class (default: "w-125")
 * @param ref - Optional ref for DOM access
 * @param justify - Alignment classes (default: "justify-center items-center")
 * @returns A styled card container
 */
export const SCard: React.FC<CardProps> = ({
  children,
  rounded = "rounded-3xl",
  outline = "outline-primary",
  shadow = "shadow-2xl",
  className,
  header,
  padding = "p-6 sm:p-12",
  height = "h-auto",
  width = "w-[95vw] sm:max-w-125",
  ref,
  justify = "justify-center items-center",
  marginOuter = "",
}) => {
  return (
    <div
      ref={ref}
      className={`${marginOuter} ${height} ${width} ${padding} ${rounded} ${shadow} flex flex-col surface-primary border ${outline} ${justify} ${className} `}
    >
      {/* Render the header if provided */}
      {header && <h2 className="h2">{header}</h2>}
      {/* Render the card content */}
      {children}
    </div>
  );
};
